import { storeToRefs } from 'pinia'
import type { ItemRow } from '~~/shared/items/itemRow'
import { hasNativeFiles } from '~~/shared/documentDrop'
import { useDocumentsStore } from '~/stores/documents'
import { useUploadTargetStore } from '~/stores/uploadTarget'

/**
 * Drop-onto-a-folder behaviour, shared by every browser surface (`BrowserItemList`,
 * `BrowserItemGrid`). Extracted rather than duplicated: the rules here are subtle — native-file
 * uploads vs internal moves, and the self/descendant/same-folder guards — and a second copy would
 * drift from them.
 *
 * Two drop kinds land on a folder:
 *  - OS files  → the #83 target picker asks whose it is, and uploads into that folder on confirm
 *  - marcus items → move into that folder (guarded by `isValidFolderDrop`)
 *
 * It owns both, end to end. It always did — which is why it survives the removal of the global
 * window drop zone (#83): a folder is a surface the user aimed at, not a catch-all.
 */
export function useFolderDrop() {
  const documents = useDocumentsStore()
  const { dropTargetId } = storeToRefs(documents)
  const { isValidFolderDrop, itemFullPath, dropOnto } = documents
  const uploadTarget = useUploadTargetStore()

  function onFolderDragOver(e: DragEvent, item: ItemRow) {
    if (item.type !== 'folder') return
    const isNative = hasNativeFiles(e.dataTransfer?.types)
    // For internal moves, reject self/descendant/same-folder/in-selection drops.
    if (!isNative && !isValidFolderDrop(item)) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer!.dropEffect = isNative ? 'copy' : 'move'
    dropTargetId.value = item.id
  }

  function onFolderDragLeave(e: DragEvent) {
    const el = e.currentTarget as HTMLElement
    if (el.contains(e.relatedTarget as Node)) return
    dropTargetId.value = null
  }

  async function onFolderDrop(e: DragEvent, item: ItemRow) {
    if (item.type !== 'folder') return
    e.preventDefault()
    e.stopPropagation()

    const files = e.dataTransfer?.files
    if (files?.length) {
      dropTargetId.value = null
      // ASK, THEN UPLOAD. A folder IS the surface the user aimed at, so this is not the
      // targetless catch-all #83 removed from the window — but a folder is a place in the tree,
      // not a client, a matter or a conversation, so the picker asks for one of those FIRST and
      // the bytes go up into the folder only once it is answered. Dismissing uploads nothing,
      // which is what stops a dropped file from becoming an unattached document (#83).
      //
      // The RAW list goes to the store, unfiltered: `requestUpload` partitions it and names
      // whatever it rejects in the dialog (#804) — filtering here first, as this used to, silently
      // dropped a disallowed file before the store ever saw its name.
      void uploadTarget.requestUpload(Array.from(files), { path: itemFullPath(item) })
      return
    }

    // Internal move: skip invalid drops (self/descendant/same-folder/in-selection).
    if (!isValidFolderDrop(item)) {
      dropTargetId.value = null
      return
    }

    try {
      dropOnto(itemFullPath(item))
    } catch (err: any) {
      console.error('[marcus:dragdrop] dropOnto failed:', err)
    }
  }

  return { onFolderDragOver, onFolderDragLeave, onFolderDrop }
}
